import React from 'react';
import { Loader2 } from 'lucide-react';

interface GoogleSignInButtonProps {
  onClick: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  id?: string;
  className?: string;
}

/**
 * Official Google Sign-In Button ("Continue with Google")
 * Uses Google's official multi-colour 'G' brand mark and follows Google Identity branding guidelines.
 * Works across desktop, mobile browsers, and installed PWA standalone mode.
 */
export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onClick,
  loading = false,
  disabled = false,
  label = 'Continue with Google',
  id,
  className = '',
}) => {
  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={label}
      className={`w-full py-2.5 px-4 bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 font-semibold rounded-xl text-xs sm:text-sm border border-slate-300 shadow-xs hover:shadow-sm transition-all flex items-center justify-center gap-3 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed select-none ${className}`}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 text-slate-600 animate-spin shrink-0" />
          <span>Connecting to Google...</span>
        </>
      ) : (
        <>
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5 shrink-0"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fill="#4285F4"
              d="M23.49 12.275c0-.85-.075-1.665-.215-2.45H12v4.635h6.445c-.28 1.5-1.125 2.77-2.395 3.62v3.01h3.875c2.265-2.085 3.565-5.16 3.565-8.815z"
            />
            <path
              fill="#34A853"
              d="M12 24c3.24 0 5.955-1.075 7.94-2.91l-3.875-3.01c-1.075.72-2.45 1.145-4.065 1.145-3.125 0-5.775-2.11-6.72-4.945H1.275v3.105C3.25 21.3 7.31 24 12 24z"
            />
            <path
              fill="#FBBC05"
              d="M5.28 14.28c-.24-.72-.375-1.49-.375-2.28s.135-1.56.375-2.28V6.615H1.275C.465 8.23 0 10.065 0 12s.465 3.77 1.275 5.385L5.28 14.28z"
            />
            <path
              fill="#EA4335"
              d="M12 4.775c1.76 0 3.34.605 4.585 1.795l3.435-3.435C17.95 1.19 15.235 0 12 0 7.31 0 3.25 2.7 1.275 6.615L5.28 9.72c.945-2.835 3.595-4.945 6.72-4.945z"
            />
          </svg>
          <span className="tracking-tight text-slate-800 font-bold">{label}</span>
        </>
      )}
    </button>
  );
};
